"use client";

import { useState } from "react";
import { useQuery, useMutation, useAction } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Alert } from "@/components/ui/alert";
import { ArrowLeft, UserCog, EyeOff, Eye, Mail, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { Row } from "@/app/v2/_components/row/Row";
import { Button } from "@/app/v2/_components/button/button";
import { Text } from "@/app/v2/_components/text/Text";

export function ImpersonationBanner() {
  const [hidden, setHidden] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const [stopping, setStopping] = useState(false);
  const impersonationState = useQuery(api.admin.impersonation.getImpersonationState);
  const stopImpersonation = useMutation(api.admin.impersonation.stopImpersonation);
  // `requestMagicLink` is the email-based, unauthenticated path. We use it
  // instead of `adminSendMagicLink` because during impersonation `ctx.auth`
  // resolves to the impersonated user, and the admin-gated path throws.
  const sendMagicLink = useAction(api.magicLinks.actions.requestMagicLink);
  const getMagicLinkUrl = useAction(api.magicLinks.actions.getMagicLinkUrl);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);

  const [magicLinkUrl, setMagicLinkUrl] = useState<string | null>(null);

  const handleStopImpersonation = async () => {
    const impersonatedUser = impersonationState?.impersonatedUser;
    const target = `/admin/users/${encodeURIComponent(impersonatedUser?.email ?? "")}`;
    setStopping(true);
    try {
      await stopImpersonation({});
      // Hard navigation (not router.push) on purpose: once the mutation
      // returns, Convex's reactive queries update synchronously and any
      // page the admin was on while impersonating (e.g. /v2/activate) may
      // fire its own router.push in the same tick based on the admin's
      // now-visible state. A client-side push here races and loses. A full
      // page navigation cannot be overridden by a subsequent router.push
      // and also resets all client state cleanly.
      window.location.assign(target);
    } catch (error) {
      setStopping(false);
      console.error("Failed to stop impersonation", error);
      toast.error("Failed to stop impersonation");
    }
  };

  const handleSendLoginLink = async () => {
    const impersonatedUser = impersonationState?.impersonatedUser;
    if (!impersonatedUser?.email) return;
    setSendingLink(true);
    try {
      await sendMagicLink({ email: impersonatedUser.email });
      toast.success(`Login link sent to ${impersonatedUser.email}`);
    } catch (error) {
      console.error("Failed to send login link", error);
      toast.error(error instanceof Error ? error.message : "Failed to send login link");
    } finally {
      setSendingLink(false);
    }
  };

  const handleCopyMagicLinkUrl = async () => {
    setIsGeneratingLink(true);
    try {
      const { magicLinkUrl } = await getMagicLinkUrl({});
      navigator.clipboard.writeText(magicLinkUrl);
      setMagicLinkUrl(magicLinkUrl);
      toast.success("Login link copied to clipboard");
    } catch (error) {
      console.error("Failed to generate login link", error);
      toast.error(error instanceof Error ? error.message : "Failed to generate login link");
    } finally {
      setIsGeneratingLink(false);
    }
  };

  if (!impersonationState || !impersonationState.isImpersonating) {
    return null;
  }

  if (hidden) {
    return (
      <button
        onClick={() => setHidden(false)}
        className="fixed top-2 right-2 z-999 p-1 rounded bg-amber-200/20 hover:bg-amber-200/60 text-amber-900/20 hover:text-amber-900/80 transition-all cursor-pointer"
        title="Show impersonation banner"
      >
        <Eye className="h-3 w-3" />
      </button>
    );
  }

  return (
    <Alert className="rounded-none border-x-0 border-t-0 bg-amber-200/20 dark:bg-amber-950 border-amber-800/10 dark:border-amber-700 sticky top-0 z-50 backdrop-blur-sm">
      <Row align between className="px-24">
        <Row align tightGap shrink>
          <UserCog className="h-5 w-5 text-amber-900/60 dark:text-amber-400" />
          <Text className="text-amber-900 dark:text-amber-400" fat>
            {impersonationState.impersonatedUser.name || impersonationState.impersonatedUser.email}
          </Text>
          <Text className="text-amber-900 dark:text-amber-400">({impersonationState.impersonatedUser.email})</Text>
          <Button
            size="sm"
            variant="outline"
            className="text-amber-900"
            onClick={handleSendLoginLink}
            disabled={sendingLink}
          >
            <Row align tightGap>
              {sendingLink ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              {sendingLink ? "Sending..." : "Send Login Link"}
            </Row>
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-amber-900 text-left max-w-[350px]"
            onClick={handleCopyMagicLinkUrl}
            disabled={isGeneratingLink}
          >
            <Row align tightGap>
              {isGeneratingLink ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
              <Text sm className="truncate line-clamp-1 text-nowrap">
                {magicLinkUrl ?? "Copy Login Link"}
              </Text>
            </Row>
          </Button>
        </Row>
        <Row align tightGap shrink>
          <Button
            size="sm"
            variant="textOnly"
            className="text-amber-900"
            onClick={handleStopImpersonation}
            disabled={stopping}
          >
            <Row align tightGap>
              {stopping ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowLeft className="h-4 w-4" />}
              Back to Admin
            </Row>
          </Button>
          <button
            onClick={() => setHidden(true)}
            className="p-1 rounded hover:bg-amber-900/10 text-amber-900/40 hover:text-amber-900/80 transition-all cursor-pointer"
            title="Hide banner for demo"
          >
            <EyeOff className="h-4 w-4" />
          </button>
        </Row>
      </Row>
    </Alert>
  );
}
